/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { columnDefs, defaultColDef } from './utils/definitions-config';
import useDebounce from '../../extras/useDebounce';
import SearchComponent from '../../extras/SearchComponent';
import { getAllCoaches } from '../../services/apiService';
import { AG_GRID_PAGESIZE } from '../../constants';
import debounce from 'lodash/debounce';
import KycDetailModal from './KycDetailModal';
import MyAccordion from '../../extras/custom/MyAccordion';
import { PageHeader } from '../../extras/PageLayout';
import useUrlFilters from '../../extras/useUrlFilters';

const KycVerification = () => {
  const {
    filters: urlFilters,
    setFilters: setUrlFilters,
    gridFilterModel,
    setGridFilterModel,
  } = useUrlFilters({
    defaultFilters: { search: '' },
    enableGridFilter: true,
  });

  const [searchString, setSearchString] = useState(urlFilters.search || '');
  const debouncedSearch = useDebounce(searchString);
  const [gridApi, setGridApi] = useState(null);
  const gridRef = useRef();

  // Keep URL in sync when search input changes
  useEffect(() => {
    setUrlFilters({ search: debouncedSearch || '' });
  }, [debouncedSearch]);

  useEffect(() => {
    gridApi && onGridReady?.(gridApi);
  }, [debouncedSearch]);

  useEffect(() => {
    if (gridRef.current?.api && gridFilterModel && Object.keys(gridFilterModel).length > 0) {
      gridRef.current.api.onFilterChanged();
    }
  }, [gridFilterModel]);

  const getServerSideDatasource = () => {
    return {
      getRows: debounce((params) => {
        let filterData = {};
        const sortData = [];
        const paginationData = {};

        if (document?.getElementById('searchText')?.value?.length) {
          filterData.searchText = document.getElementById('searchText').value;
        }

        if (params.request) {
          const { startRow, endRow, filterModel, sortModel } = params.request;
          // sorting
          if (sortModel.length) {
            sortData.push([
              sortModel[0].colId,
              sortModel[0].sort.toUpperCase(),
            ]);
          }

          // pagination
          if (endRow) {
            paginationData.pageSize = AG_GRID_PAGESIZE;
            paginationData.pageNo = startRow / AG_GRID_PAGESIZE;
          }
        }
        filterData = {
          ...filterData,
          ...(params.request && params?.request?.filterModel),
        };
        const computedData = {
          ...filterData,
          ...(Object.keys(sortData).length && { order: sortData }),
          ...paginationData,
          includeKycDetails: true,
        };

        getAllCoaches(computedData)
          .then((res) => {
            params.successCallback(res, res.length);
          })
          .catch((error) => {
            console.error(error);
            params.failCallback();
          });
      }, 1500),
    };
  };

  const onGridReady = useCallback((params, server) => {
    const datasource = getServerSideDatasource();
    params.api.setServerSideDatasource(datasource);
    setGridApi(params);
    // Restore grid filter model from URL
    if (gridFilterModel && Object.keys(gridFilterModel).length > 0) {
      params.api.setFilterModel(gridFilterModel);
    }
  }, [gridFilterModel]);

  const onFilterChanged = (params) => {
    const filterModel = params.api.getFilterModel();
    setGridFilterModel(filterModel);
  };

  const refreshKycList = () => {
    gridRef.current.api.onFilterChanged();
  };

  const LinkCellRenderer = ({ data }) => {
    const kycDetails = data.User?.KycDetail;
    kycDetails &&
      Object.assign(kycDetails, {
        firstName: data.User.firstName,
        id: data.User.id,
      });

    if (!kycDetails)
      return <i className='pointer-events-none opacity-20 far fa-eye-slash' />;

    return (
      <MyAccordion
        trigger={<i className='cursor-pointer  fas 	far fa-eye' />}
        component={
          <KycDetailModal
            coachDetail={kycDetails}
            refreshKycList={refreshKycList}
          />
        }
      />
    );
  };

  return (
    <>
      <PageHeader
        title="Coach KYCs"
        subtitle="Verify and manage coach KYC details"
      />
      <section className='bg-white py-4 px-6'>
        {/* <button onClick={onBtPageFifty}>To Page 50</button> */}

        <div className='mb-4'>
          <SearchComponent
            onChange={(e) => setSearchString(e.target.value)}
            value={searchString}
            placeholder='Search'
            style={{ backgroundColor: '#EBEBEB' }}
            className='bg-gray-50'
          />
        </div>

        {/* KYC list table */}
        <div className='ag-theme-alpine text-sm'>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            // onFirstDataRendered={onFirstDataRendered}
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            domLayout='autoHeight'
            rowModelType='serverSide'
            rowHeight={50}
            serverSideStoreType='partial'
            animateRows={true}
            frameworkComponents={{
              LinkCellRenderer,
            }}
            suppressScrollOnNewData={true}
            serverSideInfiniteScroll={true}
            pagination={true}
            paginationPageSize={AG_GRID_PAGESIZE}
            cacheBlockSize={AG_GRID_PAGESIZE}
          />
        </div>
      </section>
    </>
  );
};

export default KycVerification;
